import { speakerDisplayName, formatCourseDisplayPrice } from 'lib/speakerPresentation';
import { Dialog, DialogTrigger, DialogTitle, DialogDescription } from 'components/ui/dialog';
import { PublicDialogContent } from 'components/ui/public-dialog';
import 'components/access/AccessWorkspace.css';
import 'components/access/NavigationAccount.css';
import { resolveCoursePresentation } from 'lib/coursePresentation';
import 'components/CoursePresentation.css';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BriefcaseBusiness, ExternalLink, MapPin, Play, UserRound } from 'lucide-react';
import { CaseNav, GridGlow, Watermark, fadeUp, staggerContainer, viewportOnce } from 'components/MagellanPrototypePrimitives';
import { getPublicSpeakerBySlug } from 'lib/siteSpeakersApi';
import { listPublicCourses } from 'lib/siteCoursesApi';
import {
  formatWebinarSchedule,
  getInitials,
  getWebinarDetailPath,
} from 'lib/magellanDisplayData';

function speakerLabel(speaker) {
  return [speaker.title, speaker.company].filter(Boolean).join(' / ') || speaker.location || 'Magellan course speaker';
}

function profileSection(title, items, icon = 'briefcase') {
  const cleanItems = Array.from(new Set((items || []).filter(Boolean)));
  return cleanItems.length ? { title, items: cleanItems, icon } : null;
}

function RelatedCourseCard({ course }) {
  const presentation = resolveCoursePresentation(course);
  return <Link to={getWebinarDetailPath(course)} data-course-presentation="card" data-theme={presentation.key} style={{ '--course-accent': presentation.accent }} className="course-presentation-card course-related-card group rounded-2xl border border-ink/10 bg-ink/[0.02] p-6 transition-colors">
    <div className="flex items-center justify-between gap-4">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] course-accent-text">{formatWebinarSchedule(course)}</span>
      <ArrowRight className="h-4 w-4 text-copy/40 transition-transform group-hover:translate-x-1 course-accent-arrow" />
    </div>
    <h3 className="mt-5 font-serif text-2xl md:text-3xl leading-tight text-ink">{course.title}</h3>
    <p className="mt-4 text-sm leading-7 text-tone-400">{course.description || course.takeaway || 'Magellan course session.'}</p>
    <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-tone-500">{course.isDemo ? 'Demo outline' : 'Course outline'}</p>
    <p className="mt-4 text-sm course-accent-text">{formatCourseDisplayPrice(course)}</p>
  </Link>;
}

export default function SpeakerProfilePage({ previewRecord }) {
  const { slug } = useParams();
  // A route/preview identity change unmounts both requests and the open dialog.
  return <SpeakerProfile key={JSON.stringify([slug, previewRecord?.id])} slug={slug} previewRecord={previewRecord} />;
}

function SpeakerProfile({ slug, previewRecord }) {
  const [webinars, setWebinars] = useState([]);
  const [loadedSpeaker, setSpeaker] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [coursesStatus, setCoursesStatus] = useState('loading');
  const [open, setOpen] = useState(false);
  const speaker = previewRecord || loadedSpeaker;
  useEffect(() => {
    if (previewRecord) return undefined;
    const controller = new AbortController();
    getPublicSpeakerBySlug(slug, controller.signal)
      .then(record => { if (!controller.signal.aborted) { setSpeaker(record); setStatus('ready'); } })
      .catch(failure => { if (!controller.signal.aborted) { setError(failure.message); setStatus('error'); } });
    return () => controller.abort();
  }, [slug, previewRecord]);
  useEffect(() => {
    const controller = new AbortController();
    listPublicCourses(controller.signal)
      .then(courses => { if (!controller.signal.aborted) { setWebinars(courses); setCoursesStatus('ready'); } })
      .catch(() => { if (!controller.signal.aborted) setCoursesStatus('error'); });
    return () => controller.abort();
  }, []);
  const speakerWebinars = useMemo(() => webinars.filter(course => course.status === 'published' && speaker?.id && course.speakers?.some(person => person.status === 'published' && person.id === speaker.id)), [webinars, speaker]);
  const displayName = speakerDisplayName(speaker);
  const courseMessage = !speaker || coursesStatus === 'loading' ? 'Loading related courses…' : coursesStatus === 'error' ? 'Related courses are temporarily unavailable.' : '';
  const countMessage = !speaker || coursesStatus === 'loading' ? 'Loading training sessions…' : coursesStatus === 'error' ? 'Training sessions unavailable' : `${speakerWebinars.length} training ${speakerWebinars.length === 1 ? 'session' : 'sessions'}`;
  const speakerShortBio = speaker?.shortBio || speaker?.bio || speaker?.headline || '';
  const speakerLongBio = speaker?.fullBio || '';
  const speakerQuote = speaker?.quote || speaker?.headline || '';
  const profileSections = [
    profileSection('Credentials', speaker?.credentials, 'briefcase'),
    profileSection('Specialties', speaker?.specialties, 'user'),
    profileSection('Languages', (speaker?.languages || []).map((language) => `Language: ${language}`), 'user'),
    profileSection('Previous Experience', speaker?.previousExperience, 'briefcase'),
    profileSection('Work History', speaker?.workHistory, 'user'),
  ].filter(Boolean);
  const focusItems = Array.from(new Set((speaker?.trainingFocus || []).filter(Boolean)));

  if (!previewRecord && status === 'error') return <div className="min-h-screen bg-canvas px-6 pt-32 text-center text-ink"><p role="alert">{error}</p><Link to="/webinars" className="mt-6 inline-block text-accent-ink">Back to course library</Link></div>;
  if (!previewRecord && status === 'ready' && !speaker) {
    return <Navigate to="/webinars" replace />;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
    <div className="bg-canvas text-ink" data-testid="speaker-profile-page">
      <CaseNav backLabel="Voices" backTo="/#speakers" actionLabel="Courses" actionTo="/webinars" />
      <section className="relative pt-32 md:pt-40 pb-20 overflow-hidden grain">
        <GridGlow />
        <Watermark className="bottom-0 right-[-2%] text-[24vw] hidden lg:block">Voice</Watermark>

        <div className="site-content-shell relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="lg:sticky lg:top-28"
            >
              <div data-theme-surface="dark" className="group relative aspect-[4/5] overflow-hidden rounded-2xl border border-ink/10 bg-black">
                {speaker?.headshotUrl ? (
                  <img src={speaker.headshotUrl} alt={speaker.portraitAlt || displayName} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                ) : (
                  <div className="absolute inset-0 grid place-items-center bg-[radial-gradient(circle_at_50%_30%,rgba(76,141,204,0.22),rgba(255,255,255,0.04)_42%,rgba(0,0,0,0.78)_100%)]">
                    <span className="grid h-28 w-28 place-items-center rounded-full border border-[#4c8dcc]/35 font-serif text-4xl text-accent-ink">
                      {speaker ? getInitials(displayName) : 'MG'}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <span className="absolute bottom-5 left-5 rounded-full border border-ink/15 bg-black/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-copy/70 backdrop-blur">
                  {countMessage}
                </span>
                <DialogTrigger asChild>
                  <button type="button" disabled={!speaker} aria-label={`Courses by ${displayName || 'speaker'}`} className="absolute right-5 top-5 grid h-12 w-12 place-items-center rounded-full border border-ink/20 bg-ink/5 backdrop-blur focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4c8dcc] focus-visible:outline-offset-4">
                    <Play aria-hidden="true" className="ml-0.5 h-4 w-4 fill-[#4c8dcc] text-accent-ink" />
                  </button>
                </DialogTrigger>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-7 flex flex-col justify-center">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent-ink"
            >
              ( Voices ) — Speaker Profile
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="mt-5 font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tighter"
            >
              {displayName || 'Magellan Speaker'}
            </motion.h1>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="mt-6 flex flex-wrap gap-2.5"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-tone-300">
                <BriefcaseBusiness size={13} className="text-accent-ink" /> {speaker ? speakerLabel(speaker) : 'Loading'}
              </span>
              {speaker?.location ? (
                <span className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-tone-300">
                  <MapPin size={13} className="text-accent-ink" /> {speaker.location}
                </span>
              ) : null}
            </motion.div>
            {(speaker?.linkedInUrl || speaker?.websiteUrl) ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.62 }}
                className="mt-4 flex flex-wrap gap-2.5"
              >
                {speaker?.linkedInUrl ? (
                  <a href={speaker.linkedInUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-tone-300 transition-colors hover:border-[#4c8dcc]/50 hover:text-ink">
                    LinkedIn <ExternalLink size={12} className="text-accent-ink" />
                  </a>
                ) : null}
                {speaker?.websiteUrl ? (
                  <a href={speaker.websiteUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-tone-300 transition-colors hover:border-[#4c8dcc]/50 hover:text-ink">
                    Website <ExternalLink size={12} className="text-accent-ink" />
                  </a>
                ) : null}
              </motion.div>
            ) : null}
            {speakerQuote ? (
              <motion.blockquote
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="mt-10 max-w-2xl font-serif text-2xl md:text-3xl italic leading-snug text-tone-200"
              >
                {speakerQuote}
              </motion.blockquote>
            ) : null}
            {speakerShortBio ? (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.82 }}
                className="mt-7 max-w-2xl text-base leading-8 text-tone-400"
              >
                {speakerShortBio}
              </motion.p>
            ) : null}
          </div>
        </div>
      </section>

      {speakerLongBio ? (
        <section className="site-content-shell pb-20 md:pb-28">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="max-w-4xl border-l border-[#4c8dcc]/35 pl-6 md:pl-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-accent-ink">Full bio</p>
            <p className="mt-5 text-base leading-8 text-tone-300 md:text-lg md:leading-9">{speakerLongBio}</p>
          </motion.div>
        </section>
      ) : null}

      {profileSections.length ? (
        <section className="site-content-shell pb-20 md:pb-28">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {profileSections.map((section) => (
              <motion.div key={section.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="border border-ink/10 bg-ink/[0.025] p-7 md:p-8">
                <h2 className="font-serif text-3xl font-light text-ink">{section.title}</h2>
                <div className="mt-7 grid gap-4">
                  {section.items.map((item) => (
                    <div key={item} className="flex items-start gap-3 border-b border-ink/10 pb-4 last:border-b-0 last:pb-0">
                      {section.icon === 'briefcase' ? <BriefcaseBusiness size={16} className="mt-1 shrink-0 text-accent-ink" /> : <UserRound size={16} className="mt-1 shrink-0 text-accent-ink" />}
                      <span className="text-sm leading-6 text-tone-300">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      ) : null}

      {focusItems.length ? (
      <section className="site-content-shell py-20 md:py-28 border-t border-ink/10">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="font-serif font-light text-3xl md:text-5xl tracking-tight mb-12">
          Training <span className="italic text-accent-ink">focus</span>
        </motion.h2>
        <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-6">
          {focusItems.map((item, index) => (
            <motion.div key={`${item}-${index}`} variants={fadeUp} className="flex items-start gap-4 border-b border-ink/10 pb-5">
              <UserRound size={18} className="mt-1 shrink-0 text-accent-ink" />
              <span className="text-tone-200 leading-relaxed">{item}</span>
            </motion.div>
          ))}
        </motion.div>
      </section>
      ) : null}

      {speaker && (courseMessage || speakerWebinars.length) ? (
      <section className="bg-canvas border-y border-ink/10 py-20 md:py-28">
        <div className="site-content-shell">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="font-serif font-light text-3xl md:text-5xl tracking-tight mb-12">
            Related masterclasses
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {courseMessage ? <p role={coursesStatus === 'error' ? 'alert' : 'status'} className="text-sm leading-7 text-tone-400">{courseMessage}</p> : speakerWebinars.map(course => <RelatedCourseCard key={course.id} course={course} />)}
          </div>
        </div>
      </section>
      ) : null}
    </div>
    <PublicDialogContent className="customer-access ca-login-dialog public-dialog--courses">
      <DialogTitle>Courses by {displayName}</DialogTitle>
      <DialogDescription className="sr-only">Published courses by this speaker.</DialogDescription>
      <div className="grid gap-5">
        {courseMessage ? <p role={coursesStatus === 'error' ? 'alert' : 'status'}>{courseMessage}</p> : speakerWebinars.length ? speakerWebinars.map(course => <RelatedCourseCard key={course.id} course={course} />) : <p>0 training sessions</p>}
      </div>
    </PublicDialogContent>
    </Dialog>
  );
}
